export type HealthStatus = 'online' | 'warning' | 'danger' | 'neutral';

export interface OperationalHealthSummary {
  whatsapp: HealthStatus;
  whatsappMessage: string;
  groups: HealthStatus;
  groupsMessage: string;
  queue: HealthStatus;
  queueMessage: string;
  global: HealthStatus;
  globalMessage: string;
}

export function computeWhatsAppHealth(
  connected?: boolean,
  hasError?: boolean
): { status: HealthStatus; message: string } {
  if (hasError) {
    return {
      status: 'danger',
      message: 'WhatsApp: Falha ou sessão expirada',
    };
  }
  if (connected) {
    return {
      status: 'online',
      message: 'WhatsApp: Conectado e ativo',
    };
  }
  return {
    status: 'warning',
    message: 'WhatsApp: Desconectado (conecte o aparelho para operar)',
  };
}

export interface GroupItemLike {
  id?: string;
  status?: string;
  sync_error?: boolean;
}

export function computeGroupsHealth(
  groups: GroupItemLike[] = [],
  whatsappConnected = false
): { status: HealthStatus; message: string } {
  if (groups.length === 0) {
    return {
      status: 'neutral',
      message: 'Grupos: Nenhum grupo cadastrado',
    };
  }

  const hasGroupWithErrors = groups.some((g) => g.sync_error === true);
  if (hasGroupWithErrors) {
    return {
      status: 'warning',
      message: 'Grupos: Grupos com erro de sincronização',
    };
  }

  if (!whatsappConnected) {
    return {
      status: 'warning',
      message: 'Grupos: Sincronia em espera (WhatsApp desconectado)',
    };
  }

  const desyncedGroups = groups.filter((g) => g.status === 'desynced');
  if (desyncedGroups.length > 0) {
    return {
      status: 'warning',
      message: `Grupos: ${desyncedGroups.length} grupo(s) dessincronizado(s)`,
    };
  }

  const activeGroups = groups.filter((g) => g.status === 'active');
  if (activeGroups.length === 0) {
    return {
      status: 'warning',
      message: 'Grupos: Nenhum grupo com status ativo',
    };
  }

  return {
    status: 'online',
    message: `Grupos: ${activeGroups.length} grupo(s) pronto(s) para disparo`,
  };
}

export interface QueueItemLike {
  id?: string;
  status?: string;
}

export function computeQueueHealth(
  isWhatsAppConnected = false,
  hasActiveGroups = false,
  queueItems: QueueItemLike[] = [],
  isEnginePaused = false,
  outsideBusinessHours = false
): { status: HealthStatus; message: string } {
  // Erro crítico interno da própria fila: ofertas com falha na quarentena
  const failedItems = queueItems.filter((item) => item.status === 'failed');
  if (failedItems.length > 0) {
    return {
      status: 'danger',
      message: `Fila de Disparo: ${failedItems.length} falha(s) na quarentena`,
    };
  }

  // Se o WhatsApp estiver desconectado, a esteira apenas aguarda (neutro/em repouso dependente)
  if (!isWhatsAppConnected) {
    return {
      status: 'neutral',
      message: 'Fila de Disparo: Em espera (WhatsApp desconectado)',
    };
  }

  // Se não houver grupos ativos, a esteira aguarda destinatários
  if (!hasActiveGroups) {
    return {
      status: 'warning',
      message: 'Fila de Disparo: Em espera (Sem grupos ativos para entrega)',
    };
  }

  if (isEnginePaused) {
    return {
      status: 'neutral',
      message: 'Fila de Disparo: Pausada manualmente',
    };
  }

  if (outsideBusinessHours) {
    return {
      status: 'neutral',
      message: 'Fila de Disparo: Aguardando janela comercial',
    };
  }

  const pendingItems = queueItems.filter(
    (item) => item.status === 'scheduled' || item.status === 'pending'
  );

  if (pendingItems.length === 0) {
    return {
      status: 'neutral',
      message: 'Fila de Disparo: Em repouso (sem ofertas pendentes)',
    };
  }

  return {
    status: 'online',
    message: `Fila de Disparo: Em operação (${pendingItems.length} envio(s) pendente(s))`,
  };
}

export function computeGlobalOperationalHealth(params: {
  whatsappConnected?: boolean;
  whatsappHasError?: boolean;
  groups?: GroupItemLike[];
  queueItems?: QueueItemLike[];
  isEnginePaused?: boolean;
  outsideBusinessHours?: boolean;
}): OperationalHealthSummary {
  const wa = computeWhatsAppHealth(
    params.whatsappConnected,
    params.whatsappHasError
  );
  const grp = computeGroupsHealth(params.groups, params.whatsappConnected);

  const hasActiveGroups = (params.groups || []).some(
    (g) => g.status === 'active' && !g.sync_error
  );

  const q = computeQueueHealth(
    Boolean(params.whatsappConnected),
    hasActiveGroups,
    params.queueItems,
    params.isEnginePaused,
    params.outsideBusinessHours
  );

  const groupsList = params.groups || [];
  const hasGroupWithErrors = groupsList.some((g) => g.sync_error === true);
  const activeGroups = groupsList.filter(
    (g) => g.status === 'active' && !g.sync_error
  );
  const pendingItems = (params.queueItems || []).filter(
    (item) => item.status === 'scheduled' || item.status === 'pending'
  );

  // Diagnóstico Prioritário por Etapas (Funil Operacional)
  let global: HealthStatus = 'online';
  let globalMessage = '100% Operacional: pronto para envios';

  // 1. Etapa WhatsApp (Requisito Primário)
  if (params.whatsappHasError) {
    global = 'danger';
    globalMessage = 'WhatsApp com falha de sessão: reconecte a linha';
  } else if (!params.whatsappConnected) {
    global = 'danger';
    globalMessage = 'WhatsApp desconectado: conecte uma linha para operar';
  }
  // 2. Etapa Grupos (Destinatários)
  else if (groupsList.length === 0) {
    global = 'neutral';
    globalMessage = 'WhatsApp ativo: cadastre seu primeiro grupo';
  } else if (hasGroupWithErrors) {
    global = 'warning';
    globalMessage = 'Grupos com erro de sincronia: revise seus grupos';
  } else if (activeGroups.length === 0) {
    global = 'warning';
    globalMessage = 'Grupos inativos: ative ao menos um grupo para envios';
  }
  // 3. Etapa Fila & Esteira (Motor de Entrega e Repouso Neutro)
  else if (params.isEnginePaused) {
    global = 'neutral';
    globalMessage = 'Esteira pausada: retome os envios na aba Fila';
  } else if (params.outsideBusinessHours) {
    global = 'neutral';
    globalMessage = 'Fora da janela comercial: disparos em repouso';
  } else if (pendingItems.length === 0) {
    global = 'neutral';
    globalMessage = 'Fila em repouso: aguardando novas ofertas';
  } else {
    global = 'online';
    globalMessage = `100% Operacional: ${pendingItems.length} envio(s) programado(s)`;
  }

  return {
    whatsapp: wa.status,
    whatsappMessage: wa.message,
    groups: grp.status,
    groupsMessage: grp.message,
    queue: q.status,
    queueMessage: q.message,
    global,
    globalMessage,
  };
}
